// src/features/rrhh/rrhh/horarios/pages/HorariosPage.jsx
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Stack, Typography, Snackbar, Alert, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import HorariosToolbar from "../components/HorariosToolbar";
import HorariosFilters from "../components/HorariosFilters";
import HorariosTable from "../components/HorariosTable";
import CrearHorarioDrawer from "../components/CrearHorarioDrawer";
import EditarTurnoDialog from "../components/EditarTurnoDialog";
import DuplicarSemanaAnteriorDialog from "../components/DuplicarSemanaAnteriorDialog";
import ShiftDetailModal from "../components/ShiftDetailModal";

import {
  getEmployeeShiftsWeek,
  duplicarSemanaAnterior,
  getBranches,
  getAreas,
  getShiftDetail,
  updateShiftDetail,
  createShift,
  deleteShift,
  getEmployeeShiftOptions,
  getSectors,
  exportEmployeeShiftsPdf,
} from "../api/horariosApi";

/* Helpers */
function pad2(n) {
  return String(n).padStart(2, "0");
}
function fmtYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
// Semana Lunes->Domingo
function startOfWeekMonday(d) {
  const x = new Date(d);
  const day = x.getDay(); // 0 dom, 1 lun...
  const diff = (day === 0 ? -6 : 1 - day);
  return addDays(x, diff);
}
function endOfWeekMonday(d) {
  return addDays(startOfWeekMonday(d), 6);
}

function getWeekKey(d) {
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const monday = startOfWeekMonday(target);
  const year = monday.getFullYear();
  const month = monday.getMonth() + 1; // 1-12
  const day = monday.getDate();
  const weekOfMonth = Math.floor((day - 1) / 7) + 1;
  return year * 1000 + month * 10 + weekOfMonth;
}

function hhmm(value) {
  if (!value) return "";
  return String(value).slice(0, 5);
}

function toMinutes(value) {
  const text = hhmm(value);
  const [h, m] = text.split(":").map(Number);

  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;

  return h * 60 + m;
}

function addMinutesToHHMM(value, minutesToAdd) {
  const base = toMinutes(value);

  if (base === null) return "";

  const total = (base + minutesToAdd) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;

  return `${pad2(h)}:${pad2(m)}`;
}

function getLastEndTime(shifts = []) {
  if (!Array.isArray(shifts) || shifts.length === 0) return null;

  const sorted = [...shifts].sort((a, b) => {
    const aEnd = toMinutes(a.endTime || a.end_time) ?? 0;
    const bEnd = toMinutes(b.endTime || b.end_time) ?? 0;
    return aEnd - bEnd;
  });

  return hhmm(sorted[sorted.length - 1]?.endTime || sorted[sorted.length - 1]?.end_time);
}

function normalizeShiftForModal(shift) {
  return {
    id: shift.id ?? null,
    turnId: shift.turnId ?? shift.turn_id ?? shift.id ?? null,
    scheduleId: shift.scheduleId ?? shift.schedule_id ?? shift.id ?? null,
    startTime: hhmm(shift.startTime || shift.start_time),
    endTime: hhmm(shift.endTime || shift.end_time),
    startDayOffset: Number(shift.startDayOffset ?? shift.start_day_offset ?? 0),
    endDayOffset: Number(shift.endDayOffset ?? shift.end_day_offset ?? 0),
    branchId: shift.branchId ?? shift.branch_id ?? "",
    areaId: shift.areaId ?? shift.area_id ?? "",
    roleId: shift.roleId ?? shift.role_id ?? "",
    roleName: shift.roleName ?? shift.role_name ?? shift.role?.name ?? "",
    sectorId: shift.sectorId ?? shift.sector_id ?? shift.sectionId ?? shift.section_id ?? "",
    sectorName: shift.sectorName ?? shift.sector_name ?? shift.sectionName ?? shift.section_name ?? shift.section ?? "",
  };
}

/* UI Helpers */
function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.1,
        bgcolor: "secondary.50",
        borderRadius: "33px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}



export default function HorariosPage() {
  const navigate = useNavigate();

  // UI state
  const [cursorDate, setCursorDate] = useState(() => new Date());

  // ── Filter state (sent to backend) ──
  const [filterBranchId, setFilterBranchId] = useState("");
  const [filterAreaId, setFilterAreaId] = useState("");
  const [filterSectionId, setFilterSectionId] = useState("");

  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef(null);
  const isSearchingRef = useRef(false);

  const [shiftModalBranches, setShiftModalBranches] = useState([]);
  const [shiftModalAreas, setShiftModalAreas] = useState([]);
  const [shiftModalRoles, setShiftModalRoles] = useState([]);
  const [shiftModalSections, setShiftModalSections] = useState([]);

  const [shiftModalDefaults, setShiftModalDefaults] = useState({
    branchId: "",
    areaId: "",
    roleId: "",
    sectorId: "",
    sectorName: "",
  });

  const handleFilterSearch = useCallback((value) => {
    clearTimeout(searchTimerRef.current);
    isSearchingRef.current = true;
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(value), 350);
  }, []);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const [branchesList, setBranchesList] = useState([]);
  const [areasList, setAreasList] = useState([]);
  const [sectionsList, setSectionsList] = useState([]);

  const [employees, setEmployees] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);

  const [openCrearHorario, setOpenCrearHorario] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const [editMode] = useState(false);
  const [draftById, setDraftById] = useState({});

  const [editShiftOpen, setEditShiftOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);

  const [relatedShifts, setRelatedShifts] = useState([]);

  const [, setDraftCreates] = useState([]);
  const [, setDraftDeletes] = useState({});

  // ── ShiftDetailModal state ──
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [shiftModalMode, setShiftModalMode] = useState("view");
  const [shiftModalData, setShiftModalData] = useState(null);
  const [shiftModalAllDayShifts, setShiftModalAllDayShifts] = useState([]);
  const [shiftModalEmployeeName, setShiftModalEmployeeName] = useState("");
  const [shiftModalEmployeeId, setShiftModalEmployeeId] = useState(null);
  const [shiftModalWorkDate, setShiftModalWorkDate] = useState("");
  const [shiftModalSaving, setShiftModalSaving] = useState(false);
  const [shiftModalEmployeeWeekShifts, setShiftModalEmployeeWeekShifts] = useState([]);
  const [shiftModalInitialTurnos, setShiftModalInitialTurnos] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);

  const range = useMemo(() => {
    const s = startOfWeekMonday(cursorDate);
    const e = endOfWeekMonday(cursorDate);
    const weekKey = getWeekKey(cursorDate);

    const MESES = [
      "enero", "febrero", "marzo", "abril", "mayo", "junio",
      "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
    ];

    let label;
    if (s.getMonth() === e.getMonth()) {
      label = `Semana del ${s.getDate()} al ${e.getDate()} de ${MESES[s.getMonth()]}`;
    } else {
      label = `Semana del ${s.getDate()} de ${MESES[s.getMonth()]} al ${e.getDate()} de ${MESES[e.getMonth()]}`;
    }

    return { desde: fmtYMD(s), hasta: fmtYMD(e), weekKey, label };
  }, [cursorDate]);

  useEffect(() => {
    (async () => {
      try {
        const [b, a, s] = await Promise.all([getBranches(), getAreas(), getSectors()]);
        setBranchesList(b || []);
        setAreasList(a || []);
        setSectionsList(s || []);
      } catch (err) {
        console.error("Error loading filter reference data:", err);
        setToast({ open: true, severity: "error", message: "No se pudieron cargar los filtros (sucursales, áreas, sectores)." });
      }
    })();
  }, []);

  const handlePrev = useCallback(() => {
    setCursorDate((d) => addDays(d, -7));
  }, []);

  const handleNext = useCallback(() => {
    setCursorDate((d) => addDays(d, 7));
  }, []);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        if (!isSearchingRef.current) {
          setLoading(true);
        }
        isSearchingRef.current = false;

        const params = {
          licenseId: Number(localStorage.getItem("sabi_empresa_id")) || 1,
          weekKey: range.weekKey,
        };

        if (filterBranchId) params.branchId = filterBranchId;
        if (filterAreaId) params.areaId = filterAreaId;
        if (filterSectionId) params.sectorId = filterSectionId;
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

        const data = await getEmployeeShiftsWeek(params, { signal: ac.signal });

        setEmployees(data?.employees || []);
        setFetchError("");
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("getEmployeeShiftsWeek error:", err);
          setFetchError("No se pudieron cargar los turnos. Verificá la conexión.");
          setToast({ open: true, severity: "error", message: "Error al cargar los turnos de la semana." });
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [range.weekKey, refreshKey, filterBranchId, filterAreaId, filterSectionId, debouncedSearch]);

  const cursorLabel = useMemo(() => range.label, [range.label]);

  const weekDates = useMemo(() => {
    const monday = startOfWeekMonday(cursorDate);
    return Array.from({ length: 7 }, (_, i) => fmtYMD(addDays(monday, i)));
  }, [cursorDate]);

  const openEditShift = useCallback((sh) => {
    setSelectedShift(sh);
    setRelatedShifts([]);
    setEditShiftOpen(true);
  }, []);

  const loadEmployeeOptions = useCallback(async (employeeId) => {
    const data = await getEmployeeShiftOptions(employeeId);
  
    const branches = data?.branches || [];
    const areas = data?.areas || [];
    const roles = data?.roles || [];
    const sections = data?.sectors || data?.sections || sectionsList;
  
    setShiftModalBranches(branches);
    setShiftModalAreas(areas);
    setShiftModalRoles(roles);
    setShiftModalSections(sections);
  
    setShiftModalDefaults({
      branchId: branches.length === 1 ? Number(branches[0].id) : "",
      areaId: areas.length === 1 ? Number(areas[0].id) : "",
      roleId: roles.length === 1 ? Number(roles[0].id) : "",
      sectorId: "",
      sectorName: "",
    });
  
    return {
      ...data,
      sections,
      roles,
      defaultRoleId: roles.length === 1 ? Number(roles[0].id) : "",
      defaultSectorId: "",
      defaultSectorName: "",
    };
  }, [sectionsList]);

  const handleCellClick = useCallback(
    async ({
      employeeId,
      fullName,
      workDate,
      shift,
      allDayShifts,
      suggestedAreaId,
      suggestedSectionId,
      suggestedBranchId,
    }) => {
      try {
        setShiftModalEmployeeName(fullName || "");
        setShiftModalEmployeeId(employeeId);
        setShiftModalWorkDate(workDate);
        setShiftModalAllDayShifts(allDayShifts || []);

        const employeeWeekShifts =
          employees.find((e) => Number(e.employeeId) === Number(employeeId))?.shifts || [];

        setShiftModalEmployeeWeekShifts(employeeWeekShifts);

        const options = await loadEmployeeOptions(employeeId);

        const wantedAreaId = suggestedAreaId || filterAreaId;
        const wantedSectorId = suggestedSectionId || filterSectionId;
        const wantedBranchId = suggestedBranchId || filterBranchId;

        const areaExists = options?.areas?.some(
          (area) => String(area.id) === String(wantedAreaId)
        );

        const sectorExists = options?.sections?.some(
          (section) => String(section.id) === String(wantedSectorId)
        );

        const branchExists = options?.branches?.some(
          (branch) => String(branch.id) === String(wantedBranchId)
        );

        const existingDayShifts = Array.isArray(allDayShifts) ? allDayShifts : [];

        const fallbackBranchId =
          existingDayShifts[0]?.branchId ||
          existingDayShifts[0]?.branch_id ||
          options?.defaultBranchId ||
          "";

        const nextDefaults = {
          branchId: branchExists ? Number(wantedBranchId) : Number(fallbackBranchId) || "",
          areaId: areaExists ? Number(wantedAreaId) : Number(options?.defaultAreaId) || "",
          roleId: Number(options?.defaultRoleId) || "",
          sectorId: sectorExists ? String(wantedSectorId) : String(options?.defaultSectorId || ""),
          sectorName: "",
        };

        setShiftModalDefaults(nextDefaults);

        if (!shift) {
          const lastEndTime = getLastEndTime(existingDayShifts);
          const nextStartTime = lastEndTime || "09:00";
          const nextEndTime = addMinutesToHHMM(nextStartTime, 240) || "17:00";

          const nextTurno = {
            id: null,
            startTime: nextStartTime,
            endTime: nextEndTime,
            branchId: nextDefaults.branchId,
            areaId: nextDefaults.areaId,
            roleId: nextDefaults.roleId,
            sectorId: nextDefaults.sectorId,
            sectorName: nextDefaults.sectorName,
          };

          setShiftModalInitialTurnos([nextTurno]);
          setShiftModalMode("create");
          setShiftModalData(null);
          setShiftModalOpen(true);
          return;
        }

        setShiftModalInitialTurnos(null);

        const detail = shift.id ? await getShiftDetail(shift.id) : null;
        const scheduleId = shift.scheduleId ?? shift.schedule_id ?? shift.id;
        const scheduleTurns = employeeWeekShifts
          .filter((item) => String(item.scheduleId ?? item.schedule_id ?? item.id) === String(scheduleId))
          .map(normalizeShiftForModal)
          .sort((a, b) =>
            (Number(a.startDayOffset || 0) * 1440 + (toMinutes(a.startTime) ?? 0)) -
            (Number(b.startDayOffset || 0) * 1440 + (toMinutes(b.startTime) ?? 0))
          );

        setShiftModalMode("view");
        setShiftModalData(
          detail || {
            id: shift.id,
            turnId: shift.turnId ?? shift.turn_id ?? shift.id,
            scheduleId,
            employeeId,
            fullName,
            workDate,
            startTime: shift.scheduleStartTime || shift.startTime,
            endTime: shift.scheduleEndTime || shift.endTime,
            turns: scheduleTurns.length ? scheduleTurns : [normalizeShiftForModal(shift)],
            branchId: Number(shift.branchId ?? options?.defaultBranchId ?? ""),
            areaId: Number(shift.areaId ?? options?.defaultAreaId ?? ""),
            roleId: Number(shift.roleId ?? options?.defaultRoleId ?? ""),
            sectorId: String(
              shift.sectorId ??
              shift.sector_id ??
              shift.sectionId ??
              shift.section_id ??
              options?.defaultSectorId ??
              ""
            ),
            sectorName:
              shift.sectorName ??
              shift.sector_name ??
              shift.sectionName ??
              shift.section_name ??
              shift.section ??
              "",
          },
        );

        setShiftModalOpen(true);
      } catch (err) {
        console.error("Error loading shift modal data:", err);
        setToast({
          open: true,
          severity: "error",
          message: "No se pudo cargar la configuración del empleado.",
        });
      }
    },
    [
      loadEmployeeOptions,
      employees,
      filterAreaId,
      filterBranchId,
      filterSectionId,
    ],
  );

  /** Save del Modal: extrae sucursal, área y sector de cada turno independientemente */
  const handleShiftModalSave = useCallback(
    async (payload, mode, originalShiftId) => {
      try {
        setShiftModalSaving(true);

        const {
          turnos,
          employeeId: empId,
          workDate: wd,
          workDates = [],
          scheduleId,
          scheduleStartTime,
          scheduleEndTime,
        } = payload;

        const targetDates =
          mode === "create" && Array.isArray(workDates) && workDates.length
            ? workDates
            : [wd];

        const turns = (turnos || []).map((t) => ({
          id: t.id ?? t.turnId ?? null,
          startTime: t.startTime,
          endTime: t.endTime,
          startDayOffset: t.startDayOffset || 0,
          endDayOffset: t.endDayOffset || 0,
          branchId: t.branchId,
          areaId: t.areaId,
          roleId: t.roleId,
          sectorId: t.sectorId ?? null,
          sectorName: t.sectorName ?? null,
        }));

        if (mode === "edit") {
          await updateShiftDetail(scheduleId || originalShiftId, {
            employeeId: empId,
            workDate: wd,
            startTime: scheduleStartTime,
            endTime: scheduleEndTime,
            turns,
          });
        } else {
          for (const targetDate of targetDates) {
            await createShift({
              employeeId: empId,
              workDate: targetDate,
              startTime: scheduleStartTime,
              endTime: scheduleEndTime,
              branchId: turns[0]?.branchId,
              areaId: turns[0]?.areaId,
              roleId: turns[0]?.roleId,
              turns,
            });
          }
        }

        setShiftModalOpen(false);
        setRefreshKey((k) => k + 1);
        setToast({ open: true, severity: "success", message: "Turno guardado correctamente." });
      } catch (err) {
        console.error("Shift save error:", err);
        const msg = err?.response?.data?.message || err?.message || "No se pudo guardar el turno.";
        setToast({ open: true, severity: "error", message: msg });
      } finally {
        setShiftModalSaving(false);
      }
    },
    [],
  );

  const handleShiftModalDelete = useCallback(
    async (shiftId) => {
      if (!shiftId) return;
      try {
        setShiftModalSaving(true);
        const scheduleId =
          shiftModalData?.scheduleId ??
          shiftModalData?.schedule_id ??
          shiftModalData?.id ??
          shiftId;
        await deleteShift(scheduleId);
        setShiftModalOpen(false);
        setRefreshKey((k) => k + 1);
        setToast({ open: true, severity: "success", message: "Turno eliminado." });
      } catch (err) {
        console.error("Shift delete error:", err);
        const msg = err?.response?.data?.message || err?.message || "No se pudo eliminar el turno.";
        setToast({ open: true, severity: "error", message: msg });
      } finally {
        setShiftModalSaving(false);
      }
    },
    [shiftModalData],
  );

  function groupKeyOf(meta) {
    return `${meta?.horario_id ?? ""}|${meta?.empleado_id ?? ""}|${meta?.dia_semana ?? ""}`;
  }

  const handleConfirmUpdate = useCallback((data) => {
    if (data?.meta && ("turnos" in data) && ("removed_ids" in data)) {
      const tipo = String(data?.tipo_dia || "NORMAL").toUpperCase();
      const tipoHorario = data?.tipo_horario ? String(data.tipo_horario).toUpperCase() : undefined;

      const gk = groupKeyOf(data.meta);

      const updates = (data.turnos || [])
        .filter((t) => t?.id)
        .map((t) => {
          const u = { id: Number(t.id), tipo_dia: tipo };
          if (tipoHorario) u.tipo_horario = tipoHorario; 
          if (t.hora_entrada != null) u.hora_entrada = t.hora_entrada;
          if (t.hora_salida != null) u.hora_salida = t.hora_salida;
          return u;
        });

      setDraftById((prev) => {
        const next = { ...(prev || {}) };
        for (const u of updates) next[u.id] = { ...(next[u.id] || { id: u.id }), ...u };
        return next;
      });

      const creates = (data.turnos || [])
        .filter((t) => !t?.id)
        .map((t) => ({
          tmp_id: t.uid || `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          group_key: gk,
          horario_id: data?.meta?.horario_id ?? null,
          empleado_id: data?.meta?.empleado_id ?? null,
          dia_semana: data?.meta?.dia_semana ?? null,
          tipo_dia: tipo,
          tipo_horario: tipoHorario,
          hora_entrada: t.hora_entrada,
          hora_salida: t.hora_salida,
        }));

      setDraftCreates((prev) => {
        const kept = (prev || []).filter((c) => c.group_key !== gk);
        return [...kept, ...creates];
      });

      const removed = Array.from(new Set([...(data.removed_ids || [])].map(Number).filter(Boolean)));

      if (removed.length) {
        setDraftDeletes((prev) => {
          const next = { ...(prev || {}) };
          removed.forEach((id) => { next[String(id)] = true; });
          return next;
        });

        setDraftById((prev) => {
          const next = { ...(prev || {}) };
          removed.forEach((id) => { delete next[id]; delete next[String(id)]; });
          return next;
        });
      }

      return;
    }

    const id = data?.id;
    if (!id) return;

    setDraftById((prev) => ({
      ...(prev || {}),
      [id]: { ...(prev?.[id] || { id }), ...data },
    }));
  }, []);

  const [openDuplicar, setOpenDuplicar] = useState(false);
  const [dupScope, setDupScope] = useState("empleado"); 
  const [dupEmpleadoIds, setDupEmpleadoIds] = useState([]);
  const [dupAreaId, setDupAreaId] = useState(null);
  const [dupLoading, setDupLoading] = useState(false);

  const [toast, setToast] = useState({ open: false, severity: "info", message: "" });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const semanaInicioDupYMD = useMemo(() => fmtYMD(startOfWeekMonday(cursorDate)), [cursorDate]);

  const handleOpenDuplicar = useCallback(() => {
    setDupScope("empleado");

    const eligibleIds = (employees || [])
      .filter((e) => !Array.isArray(e.shifts) || e.shifts.length === 0)
      .map((e) => Number(e?.employeeId))
      .filter(Boolean);

    setDupEmpleadoIds(eligibleIds.length === 1 ? eligibleIds : []);
    setDupAreaId(null);
    setOpenDuplicar(true);
  }, [employees]);

  const handleConfirmDuplicar = useCallback(async () => {
    try {
      setDupLoading(true);

      const prevMonday = addDays(startOfWeekMonday(cursorDate), -7);
      const sourceWeekKey = getWeekKey(prevMonday);
      const targetWeekKey = range.weekKey;

      const payload = {
        licenseId: Number(localStorage.getItem("sabi_empresa_id")) || 1,
        sourceWeekKey,
        targetWeekKey,
        ...(dupScope === "empleado" && dupEmpleadoIds.length
          ? { employeeIds: dupEmpleadoIds.map(Number) }
          : {}),
      };

      const res = await duplicarSemanaAnterior(payload);

      setToast({
        open: true,
        severity: "success",
        message:
          res?.created > 0
            ? "Listo: se duplicó la semana anterior."
            : "No había horarios para duplicar en la semana anterior.",
      });

      setOpenDuplicar(false);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("duplicarSemanaAnterior error:", err);

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail?.message ||
        err?.message ||
        "No se pudo duplicar. Revisá que la semana destino esté vacía.";

      setToast({ open: true, severity: "error", message: msg });
    } finally {
      setDupLoading(false);
    }
  }, [dupScope, dupEmpleadoIds, cursorDate, range.weekKey]);

  const handleExportPdf = useCallback(async () => {
    if (exportingPdf) return;

    try {
      setMenuAnchor(null);

      if (loading) {
        setToast({
          open: true,
          severity: "info",
          message: "Esperá a que terminen de cargar los horarios.",
        });
        return;
      }

      if (!Array.isArray(employees) || employees.length === 0) {
        setToast({
          open: true,
          severity: "warning",
          message: "No hay empleados para exportar en esta semana.",
        });
        return;
      }

      setExportingPdf(true);

      await exportEmployeeShiftsPdf({
        licenseId: Number(localStorage.getItem("sabi_empresa_id")) || 1,
        weekKey: range.weekKey,
        branchId: filterBranchId || undefined,
        areaId: filterAreaId || undefined,
        sectorId: filterSectionId || undefined,
        search: debouncedSearch?.trim() || undefined,
      });

      setToast({
        open: true,
        severity: "success",
        message: "PDF de horarios descargado correctamente.",
      });
    } catch (err) {
      console.error("export horarios pdf error:", err);

      setToast({
        open: true,
        severity: "error",
        message: err?.message || "No se pudo descargar el PDF de horarios.",
      });
    } finally {
      setExportingPdf(false);
    }
  }, [
    exportingPdf,
    loading,
    employees,
    range.weekKey,
    filterBranchId,
    filterAreaId,
    filterSectionId,
    debouncedSearch,
  ]);

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        pb: 2,
        overflowX: "hidden",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <IconButton
          onClick={() => navigate("/rrhh/dashboard")}
          sx={{
            width: 36,
            height: 36,
            bgcolor: "secondary.500",
            "&:hover": { bgcolor: "secondary.600" },
          }}
        >
          <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
        </IconButton>
        <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
          <Pill sx={{ bgcolor: "secondary.200" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900" }}>
              Horarios
            </Typography>
          </Pill>
        </Stack>
      </Stack>

      <Box
        sx={{
          p: 2,
          bgcolor: "neutral.300",
          borderRadius: "30px",
          boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
          width: "100%",
          overflow: "hidden",
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <IconButton
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            position: "absolute",
            top: 18,
            right: 36,
            zIndex: 2,
            width: 35,
            height: 35,
            bgcolor: "neutral.50",
            borderRadius: "0px",
          }}
        >
          <MoreHorizIcon sx={{ fontSize: 28, color: "primary.900" }} />
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: { borderRadius: "14px", minWidth: 220 },
          }}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              handleOpenDuplicar();
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Duplicar horario" />
          </MenuItem>

          <MenuItem onClick={handleExportPdf} disabled={exportingPdf || loading}>
            <ListItemIcon>
              <PictureAsPdfIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary={exportingPdf ? "Exportando PDF..." : "Exportar PDF"} />
          </MenuItem>
        </Menu>

        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <HorariosToolbar
            cursorLabel={cursorLabel}
            onPrev={handlePrev}
            onNext={handleNext}
          />

          <HorariosFilters
            onSearch={handleFilterSearch}
            branchId={filterBranchId}
            onBranchId={setFilterBranchId}
            areaId={filterAreaId}
            onAreaId={setFilterAreaId}
            sectionId={filterSectionId}
            onSectionId={setFilterSectionId}
            branches={branchesList}
            areas={areasList}
            sections={sectionsList}
          />

          <HorariosTable
            loading={loading}
            fetchError={fetchError}
            cursorRange={range}
            employees={employees}
            areas={areasList}
            selectedAreaId={filterAreaId}
            selectedSectionId={filterSectionId}
            selectedBranchId={filterBranchId}
            editMode={editMode}
            draftById={draftById}
            onEditShift={openEditShift}
            onCommitDraft={handleConfirmUpdate}
            onCellClick={handleCellClick}
          />

        </Stack>
      </Box>

      <CrearHorarioDrawer
        open={openCrearHorario}
        onClose={() => setOpenCrearHorario(false)}
        empleados={employees}
        branches={branchesList}  
        areas={areasList}        
        sections={sectionsList}
        defaultCursorDate={cursorDate}
        onCreated={() => {
          setOpenCrearHorario(false);
          setRefreshKey((k) => k + 1);
        }}
      />

      <EditarTurnoDialog
        open={editShiftOpen}
        shift={selectedShift}
        relatedShifts={relatedShifts}
        onClose={() => {
          setEditShiftOpen(false);
          setRelatedShifts([]);
        }}
        onConfirmUpdate={handleConfirmUpdate}
      />

      <DuplicarSemanaAnteriorDialog
        open={openDuplicar}
        onClose={() => (dupLoading ? null : setOpenDuplicar(false))}
        empleados={employees}
        areas={areasList}
        sections={sectionsList}
        semanaInicioLabel={semanaInicioDupYMD}
        scope={dupScope}
        onScope={(s) => {
          setDupScope(s);
          setDupEmpleadoIds([]);
          setDupAreaId(null);
        }}
        empleadoIds={dupEmpleadoIds}
        onEmpleadoIds={setDupEmpleadoIds}
        areaId={dupAreaId}
        onAreaId={setDupAreaId}
        loading={dupLoading}
        onConfirm={handleConfirmDuplicar}
      />

      <ShiftDetailModal
        open={shiftModalOpen}
        onClose={() => setShiftModalOpen(false)}
        mode={shiftModalMode}
        shiftData={shiftModalData}
        allDayShifts={shiftModalAllDayShifts}
        initialCreateTurnos={shiftModalInitialTurnos}
        employeeName={shiftModalEmployeeName}
        employeeId={shiftModalEmployeeId}
        workDate={shiftModalWorkDate}
        weekKey={range.weekKey}
        weekDates={weekDates}
        employeeWeekShifts={shiftModalEmployeeWeekShifts}
        branches={shiftModalBranches}
        areas={shiftModalAreas}
        roles={shiftModalRoles}
        sections={shiftModalSections}
        defaultBranchId={shiftModalDefaults.branchId}
        defaultAreaId={shiftModalDefaults.areaId}
        defaultRoleId={shiftModalDefaults.roleId}
        defaultSectionId={shiftModalDefaults.sectorId}
        defaultSectorName={shiftModalDefaults.sectorName}
        onSave={handleShiftModalSave}
        onDelete={handleShiftModalDelete}
        saving={shiftModalSaving}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeToast} severity={toast.severity} sx={{ width: "100%", borderRadius: "14px" }}>
          {toast.message}
        </Alert>
      </Snackbar>

    </Stack>
  );
}
